import {
  Badge, Box, Button, Checkbox, Divider, Flex, Input, Text,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { PlusCircle } from "phosphor-react";
import { api } from "../../../services/api";
import { useAuth } from "../../../Hooks/useAuth";
import swal from "sweetalert";
import { FiEdit2, FiTrash2, FiCheck, FiX } from "react-icons/fi";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";

const inputProps = {
  borderRadius: "8px",
  fontSize: "13px",
  bg: "white",
  border: "1px solid rgba(0,74,51,.2)",
  _focus: { borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` },
  size: "sm",
};

function InfoRow({ label, value }) {
  if (value === undefined || value === null) return null;
  return (
    <Flex justify="space-between" align="center" py="6px" borderBottom="1px solid rgba(0,74,51,.05)">
      <Text fontSize="11px" color="gray.400" fontWeight="600">{label}</Text>
      <Text fontSize="12px" color="gray.700" fontWeight="700">{value}</Text>
    </Flex>
  );
}

export function BatchCardInfo({ batch, productId, prices }) {
  const { user } = useAuth();
  const [batchPrices, setBatchPrices]         = useState([]);
  const [newPrices, setNewPrices]             = useState([]);
  const [pricePerUnity, setPricePerUnity]     = useState("");
  const [batchUnity, setBatchUnity]           = useState("");
  const [showAddPrices, setShowAddPrices]     = useState(false);
  const [showPrevPrices, setShowPrevPrices]   = useState(false);
  const [previousPrices, setPreviousPrices]   = useState([]);
  const [editingPriceId, setEditingPriceId]   = useState(null);
  const [editUnity, setEditUnity]             = useState("");
  const [editPrice, setEditPrice]             = useState("");
  const [dataFetched, setDataFetched]         = useState(false);

  useEffect(() => {
    if (!dataFetched) {
      fetchCurrentPrices();
      setDataFetched(true);
    }
  }, [dataFetched]);

  async function fetchCurrentPrices() {
    try {
      const response = await api.get(`/api/v1/admin/panels/get_current_prices?batch_id=${batch?.id}`, {
        headers: { "access-token": user?.token, client: user?.client, uid: user?.uid, accept: "application/json" },
      });
      if (response.data.prices) {
        setBatchPrices(response.data.prices);
      }
    } catch (error) {
      console.error("Error fetching prices:", error);
    }
  }

  async function handleRegisterPrices(e) {
    e.preventDefault();
    if (newPrices.length === 0) {
      swal({ title: "Atenção", text: "Adicione pelo menos 1 preço antes de cadastrar.", icon: "warning" });
      return;
    }
    const response = await api.post("api/v1/prices", { batchPrices: newPrices }, {
      headers: { "access-token": user.token, client: user.client, uid: user.uid, accept: "application/json" },
    });
    if (response.status === 200) {
      setBatchPrices(response.data.batch_prices);
      setNewPrices([]);
      setShowAddPrices(false);
      swal({ title: "Criado!", text: "Preços adicionados com sucesso.", icon: "success" });
    } else {
      swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
    }
  }

  async function handleEditPrice(e, priceId) {
    e.preventDefault();
    const orig = batchPrices.find(p => p.id === priceId);
    const response = await api.put(`api/v1/prices/${priceId}`, {
      price_per_unity: editPrice || orig.price_per_unity,
      unity: editUnity || orig.unity,
    }, {
      headers: { "access-token": user.token, client: user.client, uid: user.uid, accept: "application/json" },
    });
    if (response.status === 200) {
      setBatchPrices(prev => prev.map(p => p.id === response.data.id ? response.data : p));
      setEditingPriceId(null);
      setEditUnity("");
      setEditPrice("");
    }
  }

  async function handleRemovePrice(e, priceId) {
    e.preventDefault();
    const response = await api.delete(`/api/v1/prices/${priceId}`);
    if (response.status === 204) {
      setBatchPrices(prev => prev.filter(p => p.id !== priceId));
      swal({ title: "Removido!", text: "Preço removido.", icon: "success" });
    } else {
      swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
    }
  }

  async function handleUsePreviousPrices(e) {
    if (!e.target.checked) { setShowPrevPrices(false); return; }
    try {
      const response = await api.get(`/api/v1/admin/panels/get_previous_prices_for_batch?product_id=${productId}`, {
        headers: { "access-token": user.token, client: user.client, uid: user.uid, accept: "application/json" },
      });
      if (response.data.prices) {
        setPreviousPrices(response.data.prices);
        setShowPrevPrices(true);
      } else {
        alert("Nenhum preço encontrado no lote anterior.");
        e.target.checked = false;
      }
    } catch {
      swal({ title: "Erro", text: "Não foi possível carregar preços anteriores.", icon: "error" });
    }
  }

  async function handleSubmitPreviousPrices(e) {
    e.preventDefault();
    const newBatchPrices = previousPrices.map(p => ({ batch_id: batch.id, price_per_unity: p.price_per_unity, unity: p.unity }));
    const response = await api.post("api/v1/prices", { batchPrices: newBatchPrices }, {
      headers: { "access-token": user.token, client: user.client, uid: user.uid, accept: "application/json" },
    });
    if (response.status === 200) {
      setBatchPrices(prev => [...prev, ...response.data.batch_prices]);
      setShowPrevPrices(false);
      swal({ title: "Criado!", text: "Preços adicionados com sucesso.", icon: "success" });
    } else {
      swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
    }
  }

  const handleAddToNewPrices = (e) => {
    e.preventDefault();
    if (!pricePerUnity || !batchUnity) return;
    setNewPrices(prev => [...prev, { batch_id: batch.id, price_per_unity: pricePerUnity, unity: batchUnity }]);
    setPricePerUnity("");
    setBatchUnity("");
  };

  const fmtDate = (d) => format(new Date(d), "dd/MM/yyyy");

  return (
    <Box>
      {/* Batch info */}
      <Box
        bg="rgba(0,74,51,.03)" borderRadius="10px" p="12px 14px" mb="16px"
        border="1px solid rgba(0,74,51,.08)"
      >
        <Text fontSize="11px" fontWeight="800" color={GREEN} textTransform="uppercase" letterSpacing="0.08em" mb="8px">
          Informações do Lote
        </Text>
        <InfoRow label="Código do lote" value={`#${batch.id}`} />
        <InfoRow label="Estoque" value={`${batch.stock} unidades`} />
        <InfoRow label="Desconto" value={`${batch.discount_per_centage}%`} />
        <InfoRow label="Início" value={fmtDate(batch.start_date)} />
        <InfoRow label="Expiração" value={fmtDate(batch.expiration_date)} />
        <InfoRow label="Unidade" value={batch.unity_of_measurement} />
      </Box>

      {/* Current prices */}
      <Box mb="14px">
        <Flex align="center" justify="space-between" mb="10px">
          <Text fontSize="12px" fontWeight="800" color={GREEN}>Tabela de preços</Text>
          <Badge colorScheme="green" fontSize="10px" borderRadius="6px">{batchPrices.length} preço{batchPrices.length !== 1 ? "s" : ""}</Badge>
        </Flex>

        {batchPrices.length === 0 ? (
          <Box bg="rgba(0,74,51,.03)" borderRadius="8px" p="12px" textAlign="center" border="1px dashed rgba(0,74,51,.15)">
            <Text fontSize="12px" color="gray.400">Nenhum preço cadastrado para este lote</Text>
          </Box>
        ) : (
          <Box bg="white" borderRadius="10px" border="1px solid rgba(0,74,51,.08)" overflow="hidden">
            {[...batchPrices].sort((a, b) => a.unity - b.unity).map((price, i) => (
              <Box key={price.id}>
                {editingPriceId === price.id ? (
                  <Flex align="center" gap="8px" px="12px" py="8px" bg="rgba(0,74,51,.03)">
                    <Input flex="1" placeholder="Qtd" type="number" value={editUnity} onChange={e => setEditUnity(e.target.value)} {...inputProps} />
                    <Input flex="1" placeholder="R$ p/ unid." type="number" value={editPrice} onChange={e => setEditPrice(e.target.value)} {...inputProps} />
                    <Button size="xs" borderRadius="6px" bg={GREEN} color={ORANGE} px="8px" onClick={e => handleEditPrice(e, price.id)}>
                      <FiCheck size={12} />
                    </Button>
                    <Button size="xs" borderRadius="6px" variant="ghost" colorScheme="gray" px="8px" onClick={() => setEditingPriceId(null)}>
                      <FiX size={12} />
                    </Button>
                  </Flex>
                ) : (
                  <Flex align="center" justify="space-between" px="12px" py="9px" _hover={{ bg: "rgba(0,74,51,.025)" }}>
                    <Box>
                      <Text fontSize="12px" fontWeight="700" color="gray.700">
                        Até {price.unity} {batch.unity_of_measurement}{price.unity > 1 ? "s" : ""}
                      </Text>
                      <Text fontSize="11px" color="gray.400">
                        {price.price_per_unity?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} / unidade
                      </Text>
                    </Box>
                    <Flex gap="6px">
                      <Button
                        size="xs" borderRadius="6px" h="24px" px="7px"
                        bg="rgba(0,74,51,.07)" color={GREEN}
                        _hover={{ bg: "rgba(0,74,51,.14)" }}
                        onClick={() => { setEditingPriceId(price.id); setEditUnity(String(price.unity)); setEditPrice(String(price.price_per_unity)); }}
                      >
                        <FiEdit2 size={11} />
                      </Button>
                      <Button
                        size="xs" borderRadius="6px" h="24px" px="7px"
                        bg="rgba(231,76,60,.07)" color="#e74c3c"
                        _hover={{ bg: "rgba(231,76,60,.14)" }}
                        onClick={e => handleRemovePrice(e, price.id)}
                      >
                        <FiTrash2 size={11} />
                      </Button>
                    </Flex>
                  </Flex>
                )}
                {i < batchPrices.length - 1 && <Divider borderColor="rgba(0,74,51,.06)" />}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Add new prices */}
      <Box mb="10px">
        <Button
          size="sm" w="100%" borderRadius="8px" variant="outline"
          colorScheme={showAddPrices ? "red" : "green"}
          fontWeight="600" fontSize="12px" mb={showAddPrices ? "10px" : "0"}
          leftIcon={<PlusCircle size={14} />}
          onClick={() => setShowAddPrices(!showAddPrices)}
        >
          {showAddPrices ? "Cancelar" : "Adicionar preços"}
        </Button>

        {showAddPrices && (
          <Box bg="white" borderRadius="10px" p="14px" border="1px solid rgba(0,74,51,.12)">
            <Text fontSize="12px" fontWeight="700" color={GREEN} mb="10px">
              Novos preços
              {newPrices.length > 0 && (
                <Badge ml="8px" colorScheme="green" fontSize="10px" borderRadius="6px">{newPrices.length} adicionado{newPrices.length !== 1 ? "s" : ""}</Badge>
              )}
            </Text>

            {newPrices.map((p, i) => (
              <Flex key={i} align="center" gap="6px" mb="6px" px="8px" py="5px" bg="rgba(0,74,51,.04)" borderRadius="6px">
                <Text fontSize="12px" flex="1" color="gray.700">
                  Até {p.unity} {batch.unity_of_measurement}{p.unity > 1 ? "s" : ""} — {parseFloat(p.price_per_unity).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}/unid.
                </Text>
                <Button size="xs" variant="ghost" colorScheme="red" px="4px" onClick={() => setNewPrices(prev => prev.filter((_, j) => j !== i))}>
                  <FiX size={11} />
                </Button>
              </Flex>
            ))}

            <Flex gap="8px" mt="10px">
              <Box flex="1">
                <Text fontSize="11px" fontWeight="700" color="gray.500" mb="3px">Qtd. mínima</Text>
                <Input type="number" placeholder="ex: 5" value={batchUnity} onChange={e => setBatchUnity(e.target.value)} {...inputProps} />
              </Box>
              <Box flex="1">
                <Text fontSize="11px" fontWeight="700" color="gray.500" mb="3px">Preço por unidade</Text>
                <Input type="number" placeholder="ex: 49.90" value={pricePerUnity} onChange={e => setPricePerUnity(e.target.value)} {...inputProps} />
              </Box>
              <Flex align="flex-end">
                <Button
                  size="sm" borderRadius="8px" bg={GREEN} color={ORANGE}
                  fontWeight="700" _hover={{ opacity: 0.88 }}
                  onClick={handleAddToNewPrices}
                >
                  +
                </Button>
              </Flex>
            </Flex>

            {newPrices.length > 0 && (
              <Button
                mt="12px" w="100%" size="sm" borderRadius="8px"
                bg={GREEN} color={ORANGE} fontWeight="700" fontSize="12px"
                _hover={{ opacity: 0.88 }}
                onClick={handleRegisterPrices}
              >
                Cadastrar {newPrices.length} preço{newPrices.length !== 1 ? "s" : ""}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Use previous prices */}
      <Box
        bg="rgba(0,74,51,.03)" borderRadius="10px" p="12px 14px"
        border="1px solid rgba(0,74,51,.08)"
      >
        <Checkbox colorScheme="green" onChange={handleUsePreviousPrices} fontSize="13px" fontWeight="600" color="gray.600">
          Copiar preços do lote anterior
        </Checkbox>

        {showPrevPrices && previousPrices.length > 0 && (
          <Box mt="10px">
            <Text fontSize="11px" fontWeight="700" color="gray.500" mb="6px">Preços do lote anterior:</Text>
            {[...previousPrices].sort((a, b) => a.unity - b.unity).map((p, i) => (
              <Text key={i} fontSize="12px" color="gray.600" mb="4px" py="4px" borderBottom="1px solid rgba(0,74,51,.06)">
                Até {p.unity} {batch.unity_of_measurement}{p.unity > 1 ? "s" : ""} — R${p.price_per_unity}/unid.
              </Text>
            ))}
            <Button
              mt="10px" size="sm" w="100%" borderRadius="8px"
              bg={GREEN} color={ORANGE} fontWeight="700" fontSize="12px"
              _hover={{ opacity: 0.88 }}
              onClick={handleSubmitPreviousPrices}
            >
              Usar estes preços
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
